import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { RULES } from "../../../Helpers/bookingRules";

// Cancel a booking. Before anything is done the server works out what cancelling NOW would refund
// (GET cancel-preview: the booking's own cancellation bands, BookingUtils.cancelPreview), and that
// breakdown is shown line by line - the same numbers the cancel then pays. The security deposit is
// always refunded in full; coupon and coins are not given back.
//
// Shown for Initiated/Booked bookings. An unpaid (Initiated) booking that turns out to have been
// paid is confirmed instead of cancelled (cancelBooking checks with Razorpay first) - that comes
// back as success:false with an explanatory message, shown as the normal error toast.

const PREVIEW_URL = (bookingId) => `/admin/booking/cancel-preview/${bookingId}`;
const CANCEL_URL = (bookingId) => `/admin/booking/cancel/${bookingId}`;

const LABELS = {
    rental: "Rental",
    deliveryFee: "Delivery fee",
    pickupFee: "Pickup fee",
    protectionFee: "Trip protection",
    platformFee: "Platform fee",
    convenienceFee: "Convenience fee",
    securityDeposit: "Security deposit",
};
const inr = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const hoursLeftText = (hours) => {
    if (!Number.isFinite(hours)) return "";
    if (hours <= 0) return "The start time has passed";
    if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min before the start`;
    return `${Math.floor(hours)} h ${Math.round((hours % 1) * 60)} min before the start`;
};

const CancelModal = ({ bookingId, onClose, onCancelled }) => {
    const [reason, setReason] = useState("");
    const [confirming, setConfirming] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewError, setPreviewError] = useState("");

    useEffect(() => {
        let alive = true;
        (async () => {
            const response = await Handler({ method: "get", url: PREVIEW_URL(bookingId) });
            if (!alive) return;
            if (response?.success) setPreview(response?.data?.data || {});
            else setPreviewError(response?.data?.message || "Couldn't work out the refund for this booking");
        })();
        return () => { alive = false; };
    }, [bookingId]);

    const submitCancel = async () => {
        setSubmitting(true);
        const response = await Handler({ method: "patch", url: CANCEL_URL(bookingId), data: { reason: reason.trim() } });
        setSubmitting(false);
        if (response?.success) {
            const refunded = response?.data?.data?.refund?.totalRefund;
            toast.success(refunded > 0 ? `Booking cancelled. ${inr(refunded)} refunded` : "Booking cancelled");
            onCancelled?.();
            onClose?.();
        } else {
            toast.error(response?.data?.message || "Could not cancel this booking");
        }
    };

    const refund = preview?.refund;
    const unpaid = preview && !refund;

    return (
        <>
            <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Cancel Booking</h5>
                            <button type="button" className="btn-close" onClick={onClose} disabled={submitting}></button>
                        </div>
                        <div className="modal-body">
                            {!preview && !previewError && <div className="text-muted small mb-3">Working out the refund...</div>}
                            {previewError && <div className="alert alert-warning py-2 px-3 small">{previewError}</div>}
                            {unpaid && <div className="alert alert-light border py-2 px-3 small">This booking hasn't been paid, so nothing is refunded. Any payment link for it is closed.</div>}
                            {refund && (
                                <div className="mb-3">
                                    <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                                        <div>
                                            <div className="fw-semibold">{refund.band?.label || "Cancellation refund"}</div>
                                            <div className="text-muted small">{hoursLeftText(refund.hoursBeforeStart)}</div>
                                        </div>
                                        <div className="text-end">
                                            <div className="text-muted small">Refund</div>
                                            <div className="fs-5 fw-semibold text-success">{inr(refund.totalRefund)}</div>
                                        </div>
                                    </div>
                                    <div className="table-responsive">
                                        <table className="table table-sm align-middle mb-1">
                                            <thead>
                                                <tr><th>Paid for</th><th className="text-end">Paid</th><th className="text-end">Refund %</th><th className="text-end">Refund</th></tr>
                                            </thead>
                                            <tbody>
                                                {(refund.lines || []).map((line) => (
                                                    <tr key={line.component}>
                                                        <td>{LABELS[line.component] || line.component}</td>
                                                        <td className="text-end">{inr(line.paid)}</td>
                                                        <td className="text-end">{line.percentage}%</td>
                                                        <td className={`text-end ${line.refund > 0 ? "" : "text-muted"}`}>{line.refund > 0 ? inr(line.refund) : "None"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr className="fw-semibold"><td>Total</td><td className="text-end">{inr(refund.totalPaid)}</td><td /><td className="text-end">{inr(refund.totalRefund)}</td></tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                    {refund.forfeited > 0 && <div className="text-muted small">{inr(refund.forfeited)} is kept under the cancellation policy. Coupon and coins are not given back.</div>}
                                </div>
                            )}
                            {!confirming ? (
                                <>
                                    <label className="form-label small">Reason (optional)</label>
                                    <textarea
                                        className="form-control"
                                        rows={3}
                                        maxLength={RULES.REASON_MAX}
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value.slice(0, RULES.REASON_MAX))}
                                        placeholder="Why is this booking being cancelled?"
                                    />
                                    <div className="form-text text-end">{reason.trim().length}/{RULES.REASON_MAX}</div>
                                </>
                            ) : (
                                <div className="alert alert-danger py-2 px-3 small mb-0">
                                    This can't be undone. Cancel this booking{refund?.totalRefund > 0 ? ` and refund ${inr(refund.totalRefund)}` : ""}{reason ? ` (reason: "${reason}")` : ""}?
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
                                Back
                            </button>
                            {!confirming ? (
                                <button type="button" className="btn btn-danger" onClick={() => setConfirming(true)} disabled={!preview && !previewError}>
                                    Continue
                                </button>
                            ) : (
                                <button type="button" className="btn btn-danger" onClick={submitCancel} disabled={submitting}>
                                    {submitting ? "Cancelling..." : "Confirm Cancellation"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};

export default CancelModal;
